import { useEffect, useState } from "react";
import { CommandMenu } from "@/components/navbar/commandMenu";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/logo";
import { Link, useRouterState } from "@tanstack/react-router";
import { ThemeSwitcher } from "@/components/navbar/themeSwitcher";
import { LanguageSwitcher } from "@/components/navbar/languageSwitcher";
import { useRouter } from "@tanstack/react-router";
import { PATHS } from "@/config/paths";
import { useMobile } from "@/providers/mobileProvider";
import { cn } from "@/lib/utils";
import { Container } from "@/components/ui/container";
import { scrollToSection } from "@/utilities/scrollToSection";
import { motion } from "framer-motion";
import { useTranslation } from "@/hooks/useTranslation";

export interface HomeNavbarProps {
  className?: string;
}

export const HomeNavbar = ({ className = "" }: HomeNavbarProps) => {
  const router = useRouter();
  const { isMobile } = useMobile();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [scrolled, setScrolled] = useState(false);
  const { t } = useTranslation();

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // If we're already on the homepage, scroll to top instead of navigating
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const isScrolled = window.scrollY > 20;
    setScrolled(isScrolled);

    let throttleTimer: number | null = null;

    const handleScroll = () => {
      if (throttleTimer !== null) return;

      throttleTimer = window.setTimeout(() => {
        const isScrolled = window.scrollY > 20;
        setScrolled(isScrolled);
        throttleTimer = null;
      }, 100);
    };

    if (pathname === PATHS.HOME) {
      const hash = window.location.hash.slice(1);
      if (hash) {
        setTimeout(() => {
          scrollToSection(hash);
          window.history.replaceState(
            "",
            document.title,
            window.location.pathname + window.location.search,
          );
        }, 100);
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (throttleTimer) window.clearTimeout(throttleTimer);
    };
  }, [pathname]);

  return (
    <div className="fixed inset-x-0 top-0 z-50 h-[72px]">
      <Container>
        <motion.nav
          aria-label="Main navigation"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            "col-span-12 px-6 py-3 flex items-center justify-between",
            "transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
            scrolled
              ? [
                  // Liquid glass effect when scrolled
                  "bg-white/10 dark:bg-black/10",
                  "backdrop-blur-xl backdrop-saturate-150",
                  "border border-white/20 dark:border-white/10",
                  "shadow-[0_8px_32px_rgba(0,0,0,0.1)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)]",
                  "mt-3 rounded-2xl",
                  // Additional glass effects
                  "ring-1 ring-white/20 dark:ring-white/10",
                  "before:absolute before:inset-0 before:rounded-2xl",
                  "before:bg-gradient-to-b before:from-white/20 before:to-transparent dark:before:from-white/10",
                  "before:pointer-events-none",
                  "relative overflow-hidden",
                ]
              : ["bg-transparent", "border border-transparent", "shadow-none"],
            isMobile && scrolled
              ? [
                  "bg-white/20 dark:bg-black/20",
                  "backdrop-blur-lg backdrop-saturate-150",
                  "shadow-[0_4px_20px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_20px_rgba(0,0,0,0.2)]",
                  "mx-2 rounded-xl",
                ]
              : "",
            className,
          )}
        >
          {/* Animated background gradient overlay for extra glass effect */}
          {scrolled && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/5 to-transparent dark:via-white/3 pointer-events-none"
            />
          )}

          <div className="flex items-center relative z-10">
            <Link
              to={PATHS.HOME}
              onClick={handleLogoClick}
              className="flex items-center gap-2 text-md font-semibold hover:opacity-80 transition-all duration-300 hover:scale-105"
            >
              <Logo className="size-8" iconClassName="size-6" showAppName />
            </Link>
          </div>

          <div className="flex items-center gap-3 relative z-10">
            <CommandMenu showButton={false} />

            <Button
              type="button"
              data-testid="start-now-button"
              variant="default"
              className={cn(
                "hidden sm:flex text-sm font-medium",
                "bg-gradient-to-r from-primary via-primary to-primary/90",
                "hover:from-primary/95 hover:via-primary/90 hover:to-primary/85",
                "hover:scale-105 active:scale-95",
                "transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
                "shadow-lg hover:shadow-xl",
                "border border-primary/20",
                // Glass effect for button
                scrolled && [
                  "backdrop-blur-sm",
                  "bg-gradient-to-r from-primary/90 via-primary/95 to-primary/90",
                ],
              )}
              onClick={() => router.navigate({ to: PATHS.DASHBOARD })}
            >
              {t("button.start_now")}
            </Button>

            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <ThemeSwitcher />
            </div>
          </div>
        </motion.nav>
      </Container>
    </div>
  );
};
